import { memo } from 'react';
import { FaScroll, FaChartBar } from 'react-icons/fa';
import { ContentType } from '@/constants/constants';
import SubscribeButton from '@/components/SubscribeButton';
import ArticleCard from '@/components/ArticleCard';
import Pagination from '@/components/elements/Pagination';
import { useIsMobile } from '@/hooks/useIsMobile';

import type { article } from '@/types/articles';
import type { DashboardData } from '@/types/util.types';
import type { ContentTypes } from '@/types/util.types';
import type { Product } from '@/types/models.types';
import type { PaginationType } from '@/types/util.types';
import NoResults from '../elements/NoResults';

interface AuthorTabContentProps {
  activeTab: ContentTypes;
  articles?: article[];
  dashboardData?: DashboardData | null;
  buttonConfig: {
    isAuthenticated: boolean;
    isSubscribed: boolean;
    products: Product | null;
    authorName?: string;
    hasUserCompletedTrial: boolean;
    onClick: () => void;
  };
  fetchArticles?: (pageNumber: number) => void;
  pagination?: PaginationType;
}

// Combined tab content component
const AuthorTabContent = ({
  activeTab,
  articles = [],
  dashboardData = null,
  buttonConfig,
  fetchArticles,
  pagination = { page: 1, pageCount: 1, pageSize: 12, total: 0 },
}: AuthorTabContentProps) => {
  switch (activeTab) {
    case ContentType.Articles:
      return <ArticlesContent articles={articles} pagination={pagination} fetchArticles={fetchArticles} />;
    case ContentType.Dashboard:
      if (dashboardData && !dashboardData.hasDashboards) {
        return null;
      }

      return (
        <DashboardContent
          dashboardData={dashboardData}
          hasAccess={dashboardData?.hasAccess ?? false}
          buttonConfig={buttonConfig}
        />
      );
    default:
      return null;
  }
};

// Articles tab content
const ArticlesContent = ({
  articles,
  pagination,
  fetchArticles,
}: {
  articles: article[];
  pagination?: PaginationType;
  fetchArticles?: (pageNumber: number) => void;
}) => {
  const isMobile = useIsMobile();

  if (!articles.length) {
    return (
      <NoResults
        icon={<FaScroll />}
        title='No articles available from this author yet'
        description='Check back at a later date or explore other authors.'
      />
    );
  }

  function handlePageChange(page: number) {
    if (fetchArticles) {
      fetchArticles(page);
    }
  }

  return (
    <div>
      <div className='w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 md:gap-6 md:mt-4'>
        {articles.map((article) => (
          <ArticleCard
            layout='noImage'
            key={article.id}
            article={article}
            background={isMobile ? 'white' : undefined}
          />
        ))}
      </div>

      {pagination && pagination.pageCount > 1 && (
        <Pagination
          page={pagination.page}
          setPage={handlePageChange}
          pageCount={pagination.pageCount}
          className='col-span-full mt-8 md:mt-10 mb-4'
        />
      )}
    </div>
  );
};

// Dashboard tab content
const DashboardContent = ({
  dashboardData,
  hasAccess,
  buttonConfig,
}: {
  dashboardData: DashboardData | null;
  hasAccess: boolean;
  buttonConfig: AuthorTabContentProps['buttonConfig'];
}) => {
  if (!hasAccess) {
    return <UpgradeSubscriptionBanner buttonConfig={buttonConfig} dashboardData={dashboardData} />;
  }

  if (!dashboardData) {
    return (
      <NoResults
        icon={<FaChartBar />}
        title='No dashboard data available'
        description='Check back at a later date or explore other authors.'
      />
    );
  }

  return (
    <div className='mt-8 border border-gray-200 rounded-lg overflow-hidden'>
      {dashboardData.dashboards?.url && (
        <div className='w-full relative aspect-[4/2.85] md:aspect-[4/2.55] lg:aspect-[4/2.45]'>
          <iframe
            src={dashboardData.dashboards.url}
            className='absolute top-0 left-0 w-full h-full border-0 aspect-video'
            title='Author Dashboard'
            allowFullScreen
          />
        </div>
      )}
    </div>
  );
};

// Subscription upgrade banner
const UpgradeSubscriptionBanner = ({
  buttonConfig,
  dashboardData,
}: {
  buttonConfig: AuthorTabContentProps['buttonConfig'];
  dashboardData: AuthorTabContentProps['dashboardData'];
}) => {
  const { hasUserCompletedTrial, isSubscribed, products, onClick, authorName } = buttonConfig;

  return (
    <div className='mt-8 p-6 bg-gray-50 rounded-lg border border-gray-200 text-center lg:mx-auto lg:w-2/3'>
      <div className='max-w-3xl mx-auto'>
        <h3 className='text-xl font-semibold mt-8 mb-2'>Subscribe to Access this Dashboard</h3>
        <p className='text-gray-600 italic mb-4 mt-4'>&quot;{dashboardData?.dashboards.description}&quot;</p>
        <p className='text-gray-600 mb-4'>
          Get complete access to {authorName || 'this author'}&apos;s research dashboard and all published content.
        </p>
      </div>
      <div className='flex justify-center'>
        {products && (
          <SubscribeButton
            isSubscribed={isSubscribed}
            price={products.price.amount}
            currency={products.price.currency}
            trialEnabled={products.trialEnabled}
            hasUserCompletedTrial={hasUserCompletedTrial}
            onClick={onClick}
          />
        )}
      </div>
      <img
        src={dashboardData?.dashboards.previewImage}
        className='w-full h-auto object-cover blur-sm mt-8'
        alt='Dashboard Preview'
      />
    </div>
  );
};

export default memo(AuthorTabContent);
